/**
 * CL-393A — Property Intelligence Workspace foundation.
 * Tabs, document categories, linked projects, and future-release placeholders.
 */

import {
  getClientPropertyStatusLabel,
  getPropertyCommunity,
  getPropertyNickname,
  isArchivedClientProperty,
  linkProjectIdToProperty,
  normalizeClientPropertyStatus,
  normalizeLinkedProjectIds,
  resolvePropertyCardAddress,
  unlinkProjectIdFromProperty,
} from "./clientMyProperties";
import { PROPERTY_WORKSPACE_DOCUMENT_CATEGORIES, PROPERTY_WORKSPACE_MEDIA_CATEGORIES } from "./myPropertyDocuments";
import { buildMyPropertiesHubPath } from "./clientMyPropertiesTabs";
import { normalizeCmaBuilderStep } from "./cmaBuilderSteps";
import {
  normalizeProfessionalCmaMode,
  PROFESSIONAL_CMA_MODES,
} from "./professionalCmaWorkspaceModel";
import {
  buildPropertyWorkspaceDestinationQuery,
  normalizePropertyWorkspaceDestination,
  PROPERTY_WORKSPACE_DESTINATIONS,
} from "./propertyWorkspaceDestinationModel";

export {
  buildAdminPropertyWorkspacePath,
  normalizeAdminPropertyWorkspaceTab,
} from "./adminPropertyWorkspaceModel";

export {
  PROPERTY_WORKSPACE_DOCUMENT_CATEGORIES,
  PROPERTY_WORKSPACE_MEDIA_CATEGORIES,
} from "./myPropertyDocuments";

export {
  linkProjectIdToProperty,
  normalizeLinkedProjectIds,
  unlinkProjectIdFromProperty,
} from "./clientMyProperties";

function safeStr(value) {
  return String(value ?? "").trim();
}

/** CL-RE-HOME-002 — Six destination groups (legacy ?tab= aliases still normalize). */
export const PROPERTY_WORKSPACE_TABS = PROPERTY_WORKSPACE_DESTINATIONS;

const TAB_IDS = new Set(PROPERTY_WORKSPACE_TABS.map((t) => t.id));

/** CL-393C / CL-394A — Intelligence sub-sections embedded in property workspace. */
export const PROPERTY_INTELLIGENCE_SECTIONS = [
  {
    id: "dna",
    label: "Property DNA",
    description: "Calgary assessment parcel facts, street view, and matched roll context.",
    hubTab: "dna",
  },
  {
    id: "market",
    label: "Market",
    description: "Assessment trends, market readiness estimates, and directional value context.",
    hubTab: "market",
  },
  {
    id: "neighbourhood",
    label: "Neighbourhood",
    description: "Community profile, schools, parks, services, and local context.",
    hubTab: "neighborhood",
  },
  {
    id: "communities",
    label: "Communities",
    description: "Your community summary and Calgary residential roll comparison table.",
    hubTab: "market",
  },
  {
    id: "development",
    label: "Development",
    description: "Nearby building and development permit activity around this property.",
    hubTab: "neighborhood",
  },
];

const INTELLIGENCE_SECTION_IDS = new Set(PROPERTY_INTELLIGENCE_SECTIONS.map((s) => s.id));

/** CL-393C — Intelligence hub cards (workspace entry points). */
export const PROPERTY_INTELLIGENCE_CARDS = [
  {
    id: "property_dna",
    title: "Property DNA",
    description: "Assessment parcel facts, street view, and matched roll context.",
    sectionId: "dna",
    hubTab: "dna",
    available: true,
  },
  {
    id: "market_analysis",
    title: "Market Analysis",
    description: "Assessment history, peer comparisons, and market readiness estimates.",
    sectionId: "market",
    hubTab: "market",
    available: true,
  },
  {
    id: "neighbourhood_intelligence",
    title: "Neighbourhood Intelligence",
    description: "Community snapshot, schools, recreation, and neighbourhood research links.",
    sectionId: "neighbourhood",
    hubTab: "neighborhood",
    available: true,
  },
  {
    id: "development_activity",
    title: "Development Activity",
    description: "Building permits and development applications near this address.",
    sectionId: "development",
    hubTab: "neighborhood",
    available: true,
  },
  {
    id: "investment_opportunities",
    title: "Investment Opportunities",
    description: "Scenario analysis and investment tracking for this property.",
    sectionId: null,
    hubTab: null,
    available: false,
  },
];

export function normalizePropertyWorkspaceTab(value) {
  const dest = normalizePropertyWorkspaceDestination(value);
  return TAB_IDS.has(dest) ? dest : "overview";
}

export function normalizePropertyIntelligenceSection(value) {
  const key = safeStr(value).toLowerCase();
  return INTELLIGENCE_SECTION_IDS.has(key) ? key : "dna";
}

export function buildPropertyWorkspacePath(
  propertyId,
  tab = "overview",
  {
    section,
    cmaStep,
    markValuationId,
    pcmaMode,
    requestId,
    filesView = null,
    marketPane = null,
    marketSection = null,
  } = {}
) {
  const id = encodeURIComponent(safeStr(propertyId));
  const rawTab = safeStr(tab).toLowerCase();
  const destination = normalizePropertyWorkspaceTab(tab);
  let resolvedMarketPane = marketPane;
  if (!resolvedMarketPane && (rawTab === "intelligence" || rawTab === "insights")) {
    resolvedMarketPane = "intelligence";
  }
  if (!resolvedMarketPane && (rawTab === "professional_cma" || rawTab === "valuation" || rawTab === "marks_valuation")) {
    resolvedMarketPane = "professional_cma";
  }
  let resolvedFilesView = filesView;
  if (!resolvedFilesView && rawTab === "media") resolvedFilesView = "media";
  if (!resolvedFilesView && rawTab === "documents") resolvedFilesView = "documents";

  const params = buildPropertyWorkspaceDestinationQuery(destination, {
    filesView: resolvedFilesView,
    marketPane: resolvedMarketPane,
    marketSection: marketSection || (rawTab === "sale_prep" ? "your_sale" : null),
    section: destination === "market_sale" ? section : null,
    cmaStep,
    markValuationId,
    pcmaMode:
      pcmaMode ||
      (rawTab === "marks_valuation" || markValuationId
        ? PROFESSIONAL_CMA_MODES.PUBLISHED
        : null),
    requestId,
    adminMode: false,
  });
  const query = params.toString();
  const base = `/client-portal/real-estate/properties/${id}`;
  return query ? `${base}?${query}` : base;
}

export function buildPropertyIntelligenceToolsPath(hubTab = "dna", extraSearch = "") {
  return buildMyPropertiesHubPath(hubTab, extraSearch);
}

export function findPropertyIntelligenceSection(sectionId) {
  const id = normalizePropertyIntelligenceSection(sectionId);
  return PROPERTY_INTELLIGENCE_SECTIONS.find((s) => s.id === id) || PROPERTY_INTELLIGENCE_SECTIONS[0];
}

export function formatPropertyWorkspaceUpdatedAt(ts) {
  try {
    if (!ts) return null;
    const d = ts.toDate ? ts.toDate() : ts.seconds ? new Date(ts.seconds * 1000) : null;
    if (!d || Number.isNaN(d.getTime())) return null;
    return d.toLocaleString();
  } catch {
    return null;
  }
}

export function enrichClientPropertyRecord(record) {
  const r = record && typeof record === "object" ? record : {};
  return {
    ...r,
    status: normalizeClientPropertyStatus(r.status),
    nickname: getPropertyNickname(r),
    community: getPropertyCommunity(r),
    cardAddress: resolvePropertyCardAddress(r),
    linkedProjectIds: normalizeLinkedProjectIds(r.linkedProjectIds),
    photoUrl: safeStr(r.photoUrl) || null,
    reportCoverPhotoUrl: safeStr(r.reportCoverPhotoUrl) || null,
    coverImageUrl: safeStr(r.coverImageUrl) || null,
    propertyCoverDocumentId: safeStr(r.propertyCoverDocumentId) || null,
    archived: isArchivedClientProperty(r),
    statusLabel: getClientPropertyStatusLabel(r.status),
  };
}

export function partitionLinkedProjects({ linkedProjectIds, availableProjects }) {
  const linked = normalizeLinkedProjectIds(linkedProjectIds);
  const byId = new Map((availableProjects || []).map((p) => [safeStr(p.id), p]));
  const active = [];
  const past = [];
  const missing = [];

  linked.forEach((pid) => {
    const project = byId.get(pid);
    if (!project) {
      missing.push({ id: pid });
      return;
    }
    if (project.isPast) past.push(project);
    else active.push(project);
  });

  const unlinked = (availableProjects || []).filter((p) => !linked.includes(safeStr(p.id)));

  return { active, past, missing, unlinked, linked };
}
